/**
 * OpenPanel Analytics Service
 *
 * Manages OpenPanel SDK lifecycle and provides methods for
 * tracking events, user identification, and global properties.
 *
 * Self-hosted OpenPanel instance: https://op.worldofwow.com
 * Only enabled on production (worldofwow.com) and localhost.
 */

import { OpenPanel } from "@openpanel/web";
import { inject } from "../../di";
import { watch } from "../../di/effects";
import type { IAuthState, ICurrentUser } from "../auth/types";
import type { IOpenPanelService, OpenPanelEventProperties, OpenPanelUserProperties } from "./types";

const OPENPANEL_API_URL = "https://op.worldofwow.com";
const OPENPANEL_CLIENT_ID = "2ea8be49-81ef-4e4f-b201-55accbf77975";
const OPENPANEL_CLIENT_SECRET = "2ea8be49-81ef-4e4f-b201-55accbf77975";
const WOW_ANONYMOUS_ID_KEY = "wowAnonymousId";

/**
 * Check if OpenPanel should be enabled in the current environment.
 * Only enabled on production and localhost.
 */
function shouldEnableOpenPanel(): boolean {
  const hostname = window.location.hostname;
  const isEnabledOnLocalhost: boolean = false;
  return (
    (isEnabledOnLocalhost && hostname === "localhost") ||
    hostname === "worldofwow.com" ||
    hostname.endsWith(".worldofwow.com")
  );
}

export class OpenPanelService implements IOpenPanelService {
  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  private initialized = false;
  private cleanupAuthWatch?: () => void;
  private op?: OpenPanel;

  isEnabled(): boolean {
    return shouldEnableOpenPanel();
  }

  initialize(): void {
    if (this.initialized) {
      return;
    }

    if (!this.isEnabled()) {
      console.log("[OpenPanel] Disabled - not on production or localhost");
      return;
    }

    this.op = new OpenPanel({
      apiUrl: OPENPANEL_API_URL,
      clientId: OPENPANEL_CLIENT_ID,
      clientSecret: OPENPANEL_CLIENT_SECRET,
      trackScreenViews: true,
      trackOutgoingLinks: true,
    });

    this.initialized = true;
    console.log("[OpenPanel] Initialized with API URL:", OPENPANEL_API_URL);

    const wowAnonymousId = this.getWowAnonymousId();
    if (wowAnonymousId) {
      this.setGlobalProperties({ wow_anonymous_id: wowAnonymousId });
    }

    const currentUser = this.authState.currentUser.get();
    if (currentUser) {
      this.identifyUser(currentUser);
    }

    this.setupAuthListeners();
  }

  track(name: string, properties?: OpenPanelEventProperties): void {
    if (!this.initialized || !this.isEnabled() || !this.op) {
      return;
    }

    this.op.track(name, properties);
  }

  identify(userId: string, properties?: OpenPanelUserProperties): void {
    if (!this.initialized || !this.isEnabled() || !this.op) {
      return;
    }

    this.op.identify({
      profileId: userId,
      firstName: properties?.name,
      properties: {
        handle: properties?.handle,
        role: properties?.role,
      },
    });
  }

  clear(): void {
    if (!this.initialized || !this.isEnabled() || !this.op) {
      return;
    }

    this.op.clear();
  }

  setGlobalProperties(properties: OpenPanelEventProperties): void {
    if (!this.initialized || !this.isEnabled() || !this.op) {
      return;
    }

    this.op.setGlobalProperties(properties);
  }

  getWowAnonymousId(): string | undefined {
    try {
      return localStorage.getItem(WOW_ANONYMOUS_ID_KEY) ?? undefined;
    } catch {
      return undefined;
    }
  }

  private setupAuthListeners(): void {
    let previousUserId: string | undefined;

    this.cleanupAuthWatch = watch(
      () => this.authState.currentUser.get(),
      (currentUser) => {
        const currentUserId = currentUser?.id;

        if (currentUserId && !previousUserId) {
          this.identifyUser(currentUser);
        } else if (!currentUserId && previousUserId) {
          this.clear();
          console.log("[OpenPanel] User cleared on sign-out");
        }

        previousUserId = currentUserId;
      },
    );
  }

  private identifyUser(user: ICurrentUser): void {
    const properties: OpenPanelUserProperties = {
      handle: user.handle,
      name: user.name,
      role: user.roles?.[0],
    };

    this.identify(user.id, properties);
    console.log("[OpenPanel] User identified:", user.id);
  }
}
